// react
import { useEffect, useMemo, useRef, useState } from 'react';
// react-router-dom
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// @mui
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  StackProps,
  TextField,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Grid from '@mui/system/Unstable_Grid/Grid';
// lodash
import { uniq } from 'lodash';
// components
import Iconify from 'src/components/iconify';
// APIs
import { findBookByAI, findBookPublic, findBooks, useGetBookCategories, useGetFields, useGetLanguages } from 'src/api/public/searchBook';
// hooks
import { useDebounce } from 'src/hooks/use-debounce';
import { useLocales } from 'src/locales';

import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { Icon } from '@iconify/react';
import { IBook } from 'src/types/DTO/book/IBook';
import Image from 'src/components/image';
import { IMAGE_HOST_API } from 'src/config-global';
import FilterTableData from '../table/action/filter-table-data';
import { useTableContext } from '../table/context';

const defaultFilters = {
  fieldIds: [],
  categoryIds: [],
  languageIds: [],
  query: '',
  page: 0,
};
let globalAbortController: AbortController | null = null;

interface BookTemp {
  bookName: string;
  photoURL: string;
  slug: string;
}

export default function GuestToolbox() {
  const { replace } = useRouter();
  const location = useLocation();
  const { t } = useLocales();
  const { setTableValue } = useTableContext();
  const [filters, setFilters] = useState(defaultFilters);

  // Ai search define
  const navigate = useNavigate();
  const prompts = [
    "Hỏi AI về tên ấn phẩm...",
    "Tác phẩm nổi tiếng của nhà văn...",
    "Giúp tôi tìm tên tác phẩm...",
    "Liệt kê 5 ấn phẩm đáng đọc 2025...",
  ];
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [placeholder, setPlaceholder] = useState("");
  const [promptIndex, setPromptIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [resultFound, setResultFound] = useState<IBook[]>([]);
  const [AiSearchQueryResult, setAiSearchQueryResult] = useState('');
  const [modelAI, setModelAI] = useState("gemini");
  // End Ai search define
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState("");
  const [recommendResult, setRecommendResult] = useState<{ bookName: string; photoURL: string; slug: string }[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const handleSelectHistory = (slug: any) => {
    navigate(paths.guest.chi_tiet_sach(slug));
  };

  const handleDeleteHistory = (name: string) => {
    const arrItem = sessionStorage.getItem("searchHistory");
    if (arrItem) {
      const parsedArr = JSON.parse(arrItem);
      const updatedArr = parsedArr.filter((book: { bookName: string }) => book.bookName !== name);
      sessionStorage.setItem("searchHistory", JSON.stringify(updatedArr));
    }
    setRecommendResult((prev) => prev.filter((book) => book.bookName !== name));
  };

  const filterDebounce = useDebounce(filters, 300);
  const [searchParams, setSearchParams] = useSearchParams();
  // const searchParams = retrieveParams();
  // Data for filter options
  const { data: bookCategoryData } = useGetBookCategories({
    unlimited: 1,
  });
  const { data: languageData } = useGetLanguages({ unlimited: 1 });
  const { data: fieldData } = useGetFields({ unlimited: 1 });

  const options = useMemo(
    () => ({
      fieldIds: uniq(
        fieldData.data.list
          ? fieldData.data.list.map((data: any) => ({ label: data.name, value: data.id }))
          : []
      ),
      categoryIds: uniq(
        bookCategoryData.data.list?.map((data: any) => ({ label: data.name, value: data.id }))
      ),
      languageIds: uniq(
        languageData.data.list
          ? languageData.data.list.map((data: any) => ({ label: data.name, value: data.id }))
          : []
      ),
    }),
    [fieldData, bookCategoryData, languageData]
  );

  const canReset =
    !!filters.query ||
    filters.categoryIds.length !== 0 ||
    filters.fieldIds.length !== 0 ||
    filters.languageIds.length !== 0;

  const syncHistoryData = async () => {
    let lstBook = [];
    const storedHistory = sessionStorage.getItem('searchHistory');

    if (storedHistory) {
      lstBook = JSON.parse(storedHistory);
      try {
        if (lstBook) {
          setRecommendResult(lstBook);
        }
      } catch (error) {
        console.error('Error fetching book result:', error);
      } finally {
        setIsLoadingHistory(false);
      }
    } else {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    const currentPrompt = prompts[promptIndex];

    if (charIndex < currentPrompt.length) {
      const timeout = setTimeout(() => {
        setPlaceholder((prev) => prev + currentPrompt.charAt(charIndex));
        setCharIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }

    const timeout = setTimeout(() => {
      setPlaceholder("");
      setCharIndex(0);
      setPromptIndex((prev) => (prev + 1) % prompts.length);
    }, 2000);
    return () => clearTimeout(timeout);
  }, [charIndex, promptIndex]);

  useEffect(() => {
    if (canReset) {
      if (location.pathname !== paths.guest.tra_cuu) {
        // console.log('okkkk');
        let params = '?';
        if (filterDebounce.categoryIds.length > 0)
          params += `categoryIds=${filterDebounce.categoryIds.toString()}&`;

        if (filterDebounce.languageIds.length > 0)
          params += `languageIds=${filterDebounce.languageIds.toString()}&`;

        if (filterDebounce.fieldIds.length > 0)
          params += `fieldIds=${filterDebounce.fieldIds.toString()}&`;

        if (filterDebounce.query !== '') params += `query=${filterDebounce.query}`;

        replace(paths.danh_cho_ban_doc.tra_cuu + params);
      } else {
        let params = {};
        if (filterDebounce.categoryIds.length > 0)
          params = { categoryIds: filterDebounce.categoryIds.toString() };
        if (filterDebounce.languageIds.length > 0)
          params = { ...params, languageIds: filterDebounce.languageIds.toString() };
        if (filterDebounce.fieldIds.length > 0)
          params = { ...params, fieldIds: filterDebounce.fieldIds.toString() };
        if (filterDebounce.query !== '') params = { ...params, query: filterDebounce.query };
        if (filterDebounce.page) params = { ...params, page: filterDebounce.page };

        setSearchParams(params);
      }
    }
  }, [filterDebounce]);

  useEffect(() => {
    setFilters({
      ...filters,
      ...getParamsFromUrl(searchParams),
    });
  }, [searchParams]);

  useEffect(() => {
    syncHistoryData();
  }, []);

  const cacheSearchHistory = async (value: string) => {
    if (value.trim() !== "") {
      setSearchHistory(value);
      const result = await findBookPublic(value);
      const dataTemp = result.data.list as BookTemp[];
      const mappedResults = dataTemp
        .map((book) => ({
          bookName: book.bookName,
          photoURL: book.photoURL,
          slug: book.slug
        }));

      const arrItem = sessionStorage.getItem("searchHistory");
      const parsedArr = arrItem ? JSON.parse(arrItem) : null;

      if (parsedArr) {
        const newItems = mappedResults.filter((newBook) => {
          return !parsedArr.some((oldBook: { bookName: string }) => oldBook.bookName === newBook.bookName);
        });
        if (newItems.length > 0) {
          const updatedHistory = [...parsedArr, ...newItems];
          sessionStorage.setItem("searchHistory", JSON.stringify(updatedHistory));
        }
      } else {
        sessionStorage.setItem("searchHistory", JSON.stringify(mappedResults));
      }
    }
  };

  const showCacheHistory = () => {
    setIsFocused(true);
    setIsLoadingHistory(true);
    syncHistoryData();
  };

  const handleFilter = (name: any, value: any) => {
    setFilters((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleShowFilterLabel = (filter: any, optionsData: any) => {
    return filter
      ?.map((tp: any) => optionsData.find((ty: any) => ty.value === tp)?.label)
      .join(', ');
  };

  const handleResetFilter = () => {
    setFilters(defaultFilters);
    setTableValue('table_data', null);
    setTableValue('table_pagination_data', null);
    searchParams.delete('query');
    searchParams.delete('fieldIds');
    searchParams.delete('languageIds');
    searchParams.delete('categoryIds');
    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  async function setAiSearchQuery(value: string) {
    setIsLoading(true);
    const abortController = new AbortController();
    globalAbortController = abortController;

    try {
      const fetchallbooks = await findBooks();
      if (fetchallbooks?.data.list.length === 0) {
        setAiSearchQueryResult('Không tìm thấy dữ liệu nào phù hợp với yêu cầu của bạn! Vui lòng thử lại');
        return;
      }
      const dataAllbooks = fetchallbooks.data.list;
      const objMapBooks = dataAllbooks.reduce((acc: Record<string, string>, book: any) => {
        acc[book.name] = book.author;
        return acc;
      }, {});

      const res = await findBookByAI(value, objMapBooks, abortController.signal, modelAI);

      if (res.done === true) {
        const rawLines = res.response.split('\n');
        const cleanedBookNames = rawLines
          .map((line: string) => line.replace(/^(\d+\.\s*|-)/, ''))
          .map((line: string) => line.replace(/\s*\([^()]*\)/g, ''))
          .map((line: string) => line.trim())
          .filter(Boolean);

        const matchedBookNames = Object.keys(objMapBooks).filter(bookName =>
          cleanedBookNames.some((cleaned: string) =>
            bookName.toLowerCase().includes(cleaned.toLowerCase()) ||
            cleaned.toLowerCase().includes(bookName.toLowerCase())
          )
        );
        const results = await Promise.all(
          matchedBookNames.map(name => findBookPublic(name))
        );
        const dataList: IBook[] = [];

        results.forEach((result, index) => {
          const books = result.data?.list || [];
          if (books.length > 0) {
            dataList.push(...books);
          }
        });

        if (dataList.length > 0) {
          setAiSearchQueryResult(`Tìm thấy ${dataList.length} ấn phẩm phù hợp với yêu cầu của bạn!`);
          const flatList = dataList.map((item: any): IBook => ({
            id: item.id,
            name: item.bookName,
            slug: item.slug,
            description: item.bookDes || '',
            otherName: item.otherName || '',
            author: item.author,
            pages: item.pages ?? 0,
            yearPublication: item.yearPublication ?? 0,
            rePublic: item.rePublic ?? 0,
            price: item.price ?? 0,
            photoURL: item.photoURL,
            createdAt: item.createdAt,
            categoryName: item.categoryName,
            publisherName: item.pubName,
            language: item.lanName,
            fieldList: item.fieldList || [],
            penaltyApplied: item.penaltyApplied ?? false,
            borrowFeeApplied: false,
            loanFee: item.loanFee ?? 0,
            attachFiles: item.attachFiles || [],
            manageInfor: [],
            reviewsList: [],
          }));
          setResultFound(flatList);
          setPrompt('');
        } else {
          setAiSearchQueryResult('Không tìm thấy dữ liệu nào phù hợp với yêu cầu của bạn! Vui lòng thử lại');
          setResultFound([]);
        }

      } else {
        console.warn('AI không phản hồi đúng:', res.done_reason);
        setAiSearchQueryResult('Đã có lỗi xảy ra vui lòng tải lại trang và thử lại!');
      }
    } catch (error: any) {
      console.warn('Search by AI has been stop.');
      setAiSearchQueryResult(`Không tìm thấy dữ liệu nào phù hợp với yêu cầu của bạn! Vui lòng thử lại`);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <Grid spacing={1} container>
        <Grid xs={12} md={3}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('Nhập từ khoá dữ liệu tìm kiếm...')}
              value={filters.query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFilter('query', e.target.value)
              }
              autoComplete='off'
              onFocus={() => showCacheHistory()}
              onBlur={() => {
                cacheSearchHistory(filters.query);
                setTimeout(() => setIsFocused(false), 200);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
            {isFocused && (
              <Paper
                elevation={3}
                sx={{
                  display: recommendResult.length === 0 ? 'none' : 'block',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  mt: 1,
                  maxHeight: 200,
                  overflowY: 'auto',
                  borderRadius: 1,
                }}
              >
                <List dense>
                  {isLoadingHistory ? (
                    <Box mt={4} mb={4} sx={{ width: '100%' }} display="flex" justifyContent="center" alignItems="center">
                      <CircularProgress color="primary" />
                    </Box>
                  ) : (
                    <>
                      {recommendResult.length > 0 && (
                        <>
                          <ListItem sx={{ paddingLeft: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{ pl: 2, color: 'text.secondary', display: 'flex', alignItems: 'center' }}
                            >
                              <Iconify icon="mdi:history" sx={{ mr: 1 }} />
                              Lịch sử tìm kiếm
                            </Typography>
                          </ListItem>
                          <ListItem divider sx={{ borderBottomStyle: 'dotted' }} />

                          {recommendResult.map((item: { bookName: string; photoURL: string; slug: string }, index: number) => (
                            <ListItem
                              key={index}
                              sx={{
                                '&:hover': { backgroundColor: 'action.hover', cursor: 'pointer' },
                              }}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                  }}
                                  onClick={() => handleDeleteHistory(item.bookName)}
                                >
                                  <Iconify icon="eva:close-outline" />
                                </IconButton>
                              }
                            >
                              <ListItemAvatar onClick={() => handleSelectHistory(item.slug)}>
                                <Avatar
                                  src={`${IMAGE_HOST_API}${item.photoURL}`}
                                  alt={item.bookName}
                                  sx={{ width: 24, height: 24, mt: 1 }}
                                />
                              </ListItemAvatar>
                              <ListItemText primary={item.bookName} onClick={() => handleSelectHistory(item.slug)} />
                            </ListItem>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </List>
              </Paper>
            )}
          </Box>
        </Grid>
        <Grid xs={4} md={3}>
          <Button
            variant="contained"
            color="inherit"
            startIcon={<Icon icon="hugeicons:ai-search" />}
            sx={{
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: "bold"
            }}
            onClick={() => setOpenDialog(true)}
          >
            Tìm kiếm bằng AI
          </Button>
        </Grid>
        <Grid xs={4} md={2}>
          <FilterTableData
            label="Lĩnh vực..."
            size="small"
            name="fieldIds"
            valueFilter={filters.fieldIds}
            onFilters={handleFilter}
            options={options.fieldIds}
            sx={{ maxWidth: 200 }}
          />
        </Grid>
        <Grid xs={4} md={2}>
          <FilterTableData
            label="Danh mục..."
            size="small"
            name="categoryIds"
            valueFilter={filters.categoryIds}
            onFilters={handleFilter}
            options={options.categoryIds}
            sx={{ maxWidth: 200 }}
          />{' '}
        </Grid>
        <Grid xs={4} md={2}>
          <FilterTableData
            label="Ngôn ngữ..."
            size="small"
            name="languageIds"
            valueFilter={filters.languageIds}
            onFilters={handleFilter}
            options={options.languageIds}
            sx={{ maxWidth: 200 }}
          />
        </Grid>
      </Grid>
      {canReset && (
        <Stack spacing={1.5} mx="auto" width={1} mt={0.5} component={Card} p={0.5} borderRadius={1}>
          <Stack flexGrow={1} spacing={1} direction="row" flexWrap="wrap" alignItems="center">
            {filters.categoryIds.length > 0 && (
              <Block label="Danh mục :">
                <Chip
                  size="small"
                  label={handleShowFilterLabel(filters.categoryIds, options.categoryIds)}
                  onDelete={() => handleFilter('categoryIds', [])}
                />
              </Block>
            )}
            {filters.fieldIds.length > 0 && (
              <Block label="Lĩnh vực :">
                <Chip
                  size="small"
                  label={handleShowFilterLabel(filters.fieldIds, options.fieldIds)}
                  onDelete={() => handleFilter('fieldIds', [])}
                />
              </Block>
            )}

            {filters.languageIds.length > 0 && (
              <Block label="Ngôn ngữ :">
                <Chip
                  size="small"
                  label={handleShowFilterLabel(filters.languageIds, options.languageIds)}
                  onDelete={() => handleFilter('languageIds', [])}
                />
              </Block>
            )}

            {filters.query !== '' && (
              <Block label="Từ khoá :">
                <Chip
                  size="small"
                  label={filters.query}
                  onDelete={() => handleFilter('query', '')}
                />
              </Block>
            )}

            <Button
              color="error"
              onClick={handleResetFilter}
              startIcon={<Iconify icon="fa:refresh" />}
            >
              Làm mới
            </Button>

          </Stack>
        </Stack>
      )}
      {/* Ai Search section */}
      <Dialog open={openDialog} maxWidth="sm" fullWidth>
        <Box sx={{ m: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <DialogTitle sx={{ flexShrink: 0 }}>Tìm kiếm bằng AI</DialogTitle>
        </Box>
        <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder={placeholder}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                setAiSearchQuery(prompt);
              }
            }}
          />
          {isLoading && (
            <Box mt={4} sx={{ width: '100%' }}>
              <LinearProgress color="primary" />
            </Box>
          )}
          {(!isLoading) && (
            <Stack spacing={2} mt={2}>
              {AiSearchQueryResult && (
                <Box sx={{ typography: 'body2', color: 'text.inherit' }}>
                  {AiSearchQueryResult}
                </Box>
              )}
              {resultFound.length > 0 && resultFound.map((item) => (
                <Card key={item.id} variant="outlined"
                  onClick={() => {
                    navigate(paths.guest.chi_tiet_sach(item.slug));
                  }}
                  sx={{
                    p: 2,
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 4,
                      backgroundColor: 'rgba(0, 0, 0, 0.03)',
                      transform: 'translateY(-2px)',
                    },
                  }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Image
                      src={IMAGE_HOST_API + item.photoURL}
                      ratio="1/1"
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1.5,
                      }}
                    />

                    <Stack spacing={1}>
                      <Box sx={{ typography: 'h6' }}>Tên sách: {item.name}</Box>
                      <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
                        Tác giả: {item.author}
                      </Box>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setIsLoading(false); setAiSearchQueryResult(''); setPrompt(''); setResultFound([]); }}
            color="inherit">
            Hủy
          </Button>
          {isLoading ? (
            <IconButton
              color="inherit"
              onClick={() => {
                if (globalAbortController) {
                  globalAbortController.abort();
                }
                setIsLoading(false);
                setAiSearchQueryResult('');
              }}
            >
              <Icon icon="icomoon-free:stop" fontSize={30} />
            </IconButton>
          ) : (
            <IconButton
              color="inherit"
              onClick={() => setAiSearchQuery(prompt)}
              disabled={prompt === ''}
              title={prompt === '' ? 'Vui lòng nhập câu hỏi' : ''}
            >
              <Icon icon="mingcute:search-ai-fill" fontSize={30} />
            </IconButton>
          )}
        </DialogActions>
      </Dialog >
    </>
  );
}

function getParamsFromUrl(searchParams: any) {
  // console.log('params: ', params);
  const query = searchParams.get('query');
  const fieldIds = searchParams.get('fieldIds');
  const languageIds = searchParams.get('languageIds');
  const categoryIds = searchParams.get('categoryIds');
  const page = searchParams.get('page');
  // console.log(query, fieldIds, languageIds, categoryIds);
  let params = {};
  if (query) {
    params = { ...params, query };
  }
  if (fieldIds) {
    params = { ...params, fieldIds: fieldIds.split(',') };
  }
  if (languageIds) {
    params = { ...params, languageIds: languageIds.split(',') };
  }
  if (categoryIds) {
    params = { ...params, categoryIds: categoryIds.split(',') };
  }
  if (page) {
    params = { ...params, page };
  }
  return params;
}
type BlockProps = StackProps & {
  label: string;
};

function Block({ label, children, sx, ...other }: BlockProps) {
  return (
    <Stack
      component={Paper}
      variant="outlined"
      spacing={1}
      direction="row"
      sx={{
        p: 1,
        borderRadius: 1,
        overflow: 'auto',
        borderStyle: 'dashed',
        ...sx,
      }}
      {...other}
    >
      <Box component="span" sx={{ typography: 'subtitle2', flexShrink: 0 }}>
        {label}
      </Box>

      <Stack spacing={1} direction="row" flexWrap="wrap">
        {children}
      </Stack>
    </Stack>
  );
}
